/**
 * Prevent multiple simultaneous calls to the asyncFunction.
 * All simultaneous calls will return the same promise
 */
export default function syncBlock<T>(name: string, asyncFunction: () => Promise<T>): () => Promise<T> {
    if (typeof BroadcastChannel === 'undefined' || navigator.locks === undefined) {
        return asyncFunction;
    }

    const channel = new BroadcastChannel(name);

    return () =>
        navigator.locks.request(name, { ifAvailable: true }, async (lock) => {
            if (!lock) {
                // If the lock is not available it means someone else is currenty executing the async function.
                // Wait for the other tab to finish their work and return the result
                const { promise, resolve } = Promise.withResolvers<T>();

                channel.addEventListener('message', (event) => resolve(event.data), { once: true });

                return promise;
            }

            const result = await asyncFunction();

            // Send the result after a delay.
            // This ensures that another instance can't enter a state where the lock is unavailable but the broadcast message has been sent.
            // It also ensures that the storageEvent has time to propagate to the other browser tabs and update the local variable.
            setTimeout(() => channel.postMessage(result), 100);

            return result;
        });
}
